import './Dashboard.css'; // CSS file

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiRequest, logout } from '../utils/api';

function Dashboard() {
    const [uploads, setUploads] = useState([]);
    const [user, setUser] = useState(null);
    const [time, setTime] = useState(""); // State for digital clock
    const [showCleanedDatasets, setShowCleanedDatasets] = useState(true); // Filter state
    const navigate = useNavigate();

    // Separate original and cleaned datasets
    // Handle both integer (1/0) and boolean/null values
    const originalDatasets = uploads.filter(upload => {
        const isCleaned = upload.is_cleaned;
        return isCleaned === 0 || isCleaned === false || isCleaned === null || isCleaned === undefined;
    });
    const cleanedDatasets = uploads.filter(upload => {
        const isCleaned = upload.is_cleaned;
        return isCleaned === 1 || isCleaned === true;
    });

    const fetchData = async () => {
        try {
            const data = await apiRequest('/api/dashboard');
            if (data.status === 'success') {
                setUser(data.user);
                // Debug: Log is_cleaned values
                console.log('Dashboard data received:', data.data.map(u => ({
                    name: u.file_name,
                    is_cleaned: u.is_cleaned,
                    type: typeof u.is_cleaned
                })));
                setUploads(data.data);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            if (error.message === 'Unauthorized') {
                navigate('/login');
            }
        }
    };

    useEffect(() => {
        // Fetch data
        fetchData();

        // Start the clock
        const interval = setInterval(() => {
            const currentTime = new Date();
            const hours = currentTime.getHours() % 12 || 12; // Convert to 12-hour format
            const minutes = currentTime.getMinutes().toString().padStart(2, "0");
            const seconds = currentTime.getSeconds().toString().padStart(2, "0");
            const ampm = currentTime.getHours() >= 12 ? "PM" : "AM";
            setTime(`${hours}:${minutes}:${seconds} ${ampm}`);
        }, 1000);

        return () => clearInterval(interval); // Cleanup interval on unmount
    }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

    const formatFileSize = (bytes) => {
        if (!bytes || bytes === 0) return 'N/A';
        const kb = bytes / 1024;
        if (kb < 1024) return `${kb.toFixed(1)} KB`;
        const mb = kb / 1024;
        return `${mb.toFixed(2)} MB`;
    };

    const formatRelativeTime = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);
        
        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    const handleLogout = async () => {
        try {
            await logout();
            navigate('/login');
        } catch (error) {
            console.error('Logout error:', error);
            // Still redirect to login even if logout fails
            navigate('/login');
        }
    };

    const handleStartAutoML = () => {
        navigate('/auto-ml'); // Navigate to AutoML page
    };

    const handleDeleteDataset = async (fileId, fileName) => {
        // Confirm deletion
        const confirmed = window.confirm(
            `Are you sure you want to delete "${fileName}"?\n\n` +
            `This will permanently delete the file and cannot be undone.` +
            (fileName.includes('_cleaned') ? '' : '\n\nNote: Any cleaned datasets created from this file will also be deleted.')
        );
        
        if (!confirmed) {
            return;
        }
        
        try {
            const response = await apiRequest(`/api/dashboard/dataset/${fileId}`, {
                method: 'DELETE'
            });
            
            if (response.status === 'success') {
                // Refresh the data
                await fetchData();
            } else {
                alert('Failed to delete dataset: ' + (response.message || 'Unknown error'));
            }
        } catch (error) {
            console.error('Error deleting dataset:', error);
            alert('Failed to delete dataset: ' + (error.message || 'Unknown error'));
        }
    };

    return (
        <div className="dashboard">
            {/* Top Navigation Bar */}
            <div className="top-nav">
                <div className="nav-content">
                    <div className="nav-logo" onClick={() => navigate("/")} style={{ cursor: "pointer" }}>
                        <img src="/HDASLogo.png" alt="HDAS" style={{ height: "80px", width: "auto" }} />
                    </div>
                    <div className="nav-right">
                        <div className="digital-clock">{time}</div>
                <button onClick={handleLogout} className="logout-button">Logout</button>
                    </div>
                </div>
            </div>

            {/* Welcome Section - Compact */}
            <div className="welcome-section">
                <div className="welcome-content">
                    <div>
                        <h1>Welcome back, {user ? user.name : "Loading..."}</h1>
                        <p className="welcome-subtitle">Healthcare Data Analysis Workspace</p>
                    </div>
                    <button onClick={handleStartAutoML} className="quick-action-button">
                        Start Analysis
                    </button>
                </div>
            </div>

            {/* Main Content - Compact Grid */}
            <div className="dashboard-content">
                {/* Stats Row */}
                <div className="stats-row">
                    <div className="stat-card">
                        <div className="stat-icon icon-stats-card"></div>
                        <div className="stat-content">
                            <div className="stat-value">{uploads.length}</div>
                            <div className="stat-label">Total Datasets</div>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon icon-file-card"></div>
                        <div className="stat-content">
                            <div className="stat-value">CSV</div>
                            <div className="stat-label">File Format</div>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon icon-user-card"></div>
                        <div className="stat-content">
                            <div className="stat-value">{user ? user.name.split(' ')[0] : 'N/A'}</div>
                            <div className="stat-label">User</div>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon icon-time-card"></div>
                        <div className="stat-content">
                            <div className="stat-value">{uploads.length > 0 ? formatRelativeTime(uploads[0].upload_date) : 'N/A'}</div>
                            <div className="stat-label">Last Upload</div>
                        </div>
                    </div>
                </div>

                {/* Datasets Sections - Split into Two Equal Sections */}
                <div className="datasets-sections-container">
                    {/* Original Datasets Section */}
                    <div className="section datasets-section">
                        <div className="section-header">
                            <h2>Original Datasets ({originalDatasets.length})</h2>
                            <button onClick={handleStartAutoML} className="secondary-button">Upload New</button>
                        </div>
                        {originalDatasets.length > 0 ? (
                            <div className="datasets-table">
                                <div className="table-header">
                                    <span className="col-name">File Name</span>
                                    <span className="col-size">Size</span>
                                    <span className="col-date">Uploaded</span>
                                    <span className="col-action">Action</span>
                                </div>
                                <div className="table-body">
                                    {originalDatasets.slice(0, 8).map((upload, index) => {
                                        const fileSize = formatFileSize(upload.file_size);
                                        return (
                                            <div key={index} className="table-row">
                                                <span className="col-name" title={upload.file_name}>
                                                    {upload.file_name.length > 30 
                                                        ? upload.file_name.substring(0, 30) + '...' 
                                                        : upload.file_name}
                                                </span>
                                                <span className="col-size">{fileSize}</span>
                                                <span className="col-date">{formatRelativeTime(upload.upload_date)}</span>
                                                <div className="col-action">
                                                    <a 
                                                        href={upload.download_link} 
                                                        target="_blank" 
                                                        rel="noopener noreferrer"
                                                        className="table-link"
                                                    >
                                                        Download
                                                    </a>
                                                    <button
                                                        onClick={() => handleDeleteDataset(upload.file_id, upload.file_name)}
                                                        className="delete-button"
                                                        title="Delete dataset"
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                {originalDatasets.length > 8 && (
                                    <div className="table-footer">
                                        <span>Showing 8 of {originalDatasets.length} datasets</span>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="empty-state">
                                <p className="empty-message">No datasets uploaded yet</p>
                                <button onClick={handleStartAutoML} className="secondary-button">Upload Your First Dataset</button>
                            </div>
                        )}
                    </div>

                    {/* Cleaned Datasets Section */}
                    <div className="section cleaned-datasets-section">
                        <div className="section-header">
                            <h2>Cleaned Datasets ({cleanedDatasets.length})</h2>
                            <label className="filter-toggle">
                                <input
                                    type="checkbox"
                                    checked={showCleanedDatasets}
                                    onChange={(e) => setShowCleanedDatasets(e.target.checked)}
                                />
                                <span>Show</span>
                            </label>
                        </div>
                        {showCleanedDatasets ? (
                            cleanedDatasets.length > 0 ? (
                                <div className="datasets-table">
                                    <div className="table-header">
                                        <span className="col-name">File Name</span>
                                        <span className="col-size">Size</span>
                                        <span className="col-method">Method</span>
                                        <span className="col-date">Created</span>
                                        <span className="col-action">Action</span>
                                    </div>
                                    <div className="table-body">
                                        {cleanedDatasets.slice(0, 8).map((upload, index) => {
                                            const fileSize = formatFileSize(upload.file_size);
                                            let methodDisplay = 'Custom';
                                            try {
                                                const methodJson = upload.cleaning_method ? JSON.parse(upload.cleaning_method) : null;
                                                if (methodJson && typeof methodJson === 'object') {
                                                    const methods = Object.values(methodJson);
                                                    const uniqueMethods = [...new Set(methods)];
                                                    if (uniqueMethods.length === 1) {
                                                        methodDisplay = uniqueMethods[0].charAt(0).toUpperCase() + uniqueMethods[0].slice(1);
                                                    } else {
                                                        methodDisplay = `${uniqueMethods.length} methods`;
                                                    }
                                                }
                                            } catch (e) {
                                                // If not JSON, treat as old format
                                                methodDisplay = upload.cleaning_method || 'Unknown';
                                            }
                                            return (
                                                <div key={index} className="table-row">
                                                    <span className="col-name" title={upload.file_name}>
                                                        {upload.file_name.length > 25 
                                                            ? upload.file_name.substring(0, 25) + '...' 
                                                            : upload.file_name}
                                                    </span>
                                                    <span className="col-size">{fileSize}</span>
                                                    <span className="col-method">
                                                        <span className="method-badge">{methodDisplay}</span>
                                                    </span>
                                                    <span className="col-date">{formatRelativeTime(upload.upload_date)}</span>
                                                    <div className="col-action">
                                                        <a 
                                                            href={upload.download_link} 
                                                            target="_blank" 
                                                            rel="noopener noreferrer"
                                                            className="table-link"
                                                        >
                                                            Download
                                                        </a>
                                                        <button
                                                            onClick={() => handleDeleteDataset(upload.file_id, upload.file_name)}
                                                            className="delete-button"
                                                            title="Delete dataset"
                                                        >
                                                            Delete
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    {cleanedDatasets.length > 8 && (
                                        <div className="table-footer">
                                            <span>Showing 8 of {cleanedDatasets.length} cleaned datasets</span>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="empty-state">
                                    <p className="empty-message">No cleaned datasets yet</p>
                                    <p className="empty-submessage">Clean a dataset from the Dataset Preview page to see it here</p>
                                </div>
                            )
                        ) : (
                            <div className="empty-state">
                                <p className="empty-message">Cleaned datasets are hidden</p>
                                <p className="empty-submessage">Check the "Show" toggle above to display them</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Quick Actions & Healthcare Info Section */}
                <div className="sections-grid">
                    <div className="section actions-section">
                        <h2>Quick Actions</h2>
                        <div className="actions-list">
                            <button onClick={handleStartAutoML} className="action-button">
                                <span className="action-icon icon-upload-action"></span>
                                <span className="action-text">Upload New Dataset</span>
                            </button>
                            {uploads.length > 0 && (
                                <>
                                    <button onClick={() => navigate('/dataset-preview')} className="action-button">
                                        <span className="action-icon icon-preview-action"></span>
                                        <span className="action-text">View Dataset Preview</span>
                                    </button>
                                    <button onClick={() => navigate('/analysis-selection')} className="action-button">
                                        <span className="action-icon icon-analysis-action"></span>
                                        <span className="action-text">Start Analysis</span>
                                    </button>
                                    <button onClick={() => navigate('/dashboard')} className="action-button">
                                        <span className="action-icon icon-dashboard-action"></span>
                                        <span className="action-text">View All Datasets</span>
                                    </button>
                                </>
                            )}
                        </div>
                        <div className="info-box">
                            <h3>Quick Tip</h3>
                            <p>Upload CSV files with patient data, clinical trials, or research datasets. CSV format is required for analysis.</p>
                        </div>
                    </div>

                    <div className="section healthcare-info-section">
                        <h2>Healthcare Data Analysis</h2>
                        <div className="healthcare-info-content">
                            <div className="info-item">
                                <div className="info-icon icon-data-quality"></div>
                                <div className="info-content">
                                    <h4>Data Quality</h4>
                                    <p>Ensure your datasets are clean and properly formatted. Missing values are automatically detected and can be handled during the cleaning process.</p>
                                </div>
                            </div>
                            <div className="info-item">
                                <div className="info-icon icon-privacy"></div>
                                <div className="info-content">
                                    <h4>Privacy & Security</h4>
                                    <p>All datasets are stored securely and are only accessible to you. Patient data should be anonymized before upload.</p>
                                </div>
                            </div>
                            <div className="info-item">
                                <div className="info-icon icon-analysis"></div>
                                <div className="info-content">
                                    <h4>Analysis Types</h4>
                                    <p>Choose between supervised learning (classification/regression) or unsupervised learning (clustering) based on your research needs.</p>
                                </div>
                            </div>
                        </div>
                        <div className="info-box healthcare-tip">
                            <h3>Best Practices</h3>
                            <p>For best results, use datasets with at least 100 rows and ensure your target variable is clearly defined. Clean datasets produce more accurate models.</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Dashboard;
